import AttachMoneyRounded from "@mui/icons-material/AttachMoneyRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import { amber, green, grey, orange, red } from "@mui/material/colors";
import { AgGauge } from "ag-charts-react";
import { AgGridReact } from "ag-grid-react";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import isNumber from "lodash/isNumber";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { columnDefSuppressHeaderFilterButton } from "../../_src_shared/agGrid/columnDef/columnDefSuppressHeaderFilterButton";
import { useColumnTypes } from "../../_src_shared/utils/agGrid/columnTypes/hooks/useColumnTypes";
import AgGridTableWrapper from "../../components/agGrid/AgGridTableWrapper";
import JsonPreview from "../../components/common/JsonPreview";
import Flx from "../../components/layout/Flx";
import Htag from "../../components/typography/Htag";
import Txt from "../../components/typography/Txt";
import { formatDollar } from "../../utils/numbers/formatDollar";

const cellNameFontWeight = 300;
const LoanIntakePricingSizerResultsCard = ({
  formSpy,
  results,
  isHandsomeHomebuyer,
}) => {
  if (isNil(results)) {
    return null;
  }
  return (
    <RootCard results={results}>
      <HeaderSection results={results} />
      <Flx column gap={6}>
        <Flx column gap={2}>
          <Flx fw jb>
            <Flx column gap={1.5}>
              <SecondaryLabel>Guideline Check</SecondaryLabel>
              <ResultRowListItem
                title={"Hard Exceptions"}
                items={results?.hard_exceptions}
              />
            </Flx>
          </Flx>
          <ResultRowListItem
            title={"Exception Approvals Required"}
            items={results?.exception_approvals_required}
          />
          <ResultRowListItem
            title={"Leverage Reductions"}
            items={results?.leverage_reductions}
          />
        </Flx>

        <Flx column gap={1}>
          <Flx sx={{ justifyContent: "space-between" }} ac wrap gap={2}>
            <HighlightItem
              label={"Total Leverage Reduction"}
              type="percent"
              value={results?.total_leverage_reduction}
            />
            <HighlightItem
              label={"Originator Origination Points"}
              type={"float"}
              value={results?.originatorOriginationPoints}
            />
            <HighlightItem
              label={"Total Origination Points"}
              type={"float"}
              value={results?.totalOriginationPoints}
            />
          </Flx>
          <MaxLeverageTable results={results} />
        </Flx>

        <TwoColumnRow gap={4} sx>
          <BorrowerClassificationGroup results={results} />
          <LoanBreakdownGroup results={results} />
        </TwoColumnRow>

        <TwoColumnRow gap={4}>
          <ZillowGroup results={results} />
          {isHandsomeHomebuyer ? <InterestDataGroup results={results} /> : null}
        </TwoColumnRow>
      </Flx>
      {formSpy ? (
        <Box sx={{ mt: 2, background: grey[50], p: 2 }}>
          <JsonPreview values={results} defaultCollapse={3} />
        </Box>
      ) : null}
    </RootCard>
  );
};

/**
 * LABELED GROUPS
 */

const BorrowerClassificationGroup = ({ results }) => {
  return (
    <Group sx={{ flexGrow: 1 }} title={"Borrower Classification"}>
      <MinimalCard sx={{ flexGrow: 1 }}>
        <Flx column jb ac sx={{ flexGrow: 1 }}>
          <BorrowerClassificationTable results={results} />

          <Flx fw sx={{ px: 0, py: 3 }} center>
            <GradeRadialDial
              grade={results?.sizerBorrowers?.rtl_borrower_classification_score}
            />
          </Flx>
        </Flx>
      </MinimalCard>
    </Group>
  );
};

const LoanBreakdownGroup = ({ results }) => {
  return (
    <Group
      title={"Loan Breakdown"}
      sx={{ flexGrow: 1, flexShrink: 0, flexBasis: "345px" }}
    >
      <LoanBreakdownTable results={results} />
    </Group>
  );
};

const ZillowGroup = ({ results }) => {
  return (
    <Group sx={{ flexGrow: 1 }} title={"Property Zillow Data"}>
      <ZillowDataTable results={results} />
    </Group>
  );
};

const InterestDataGroup = ({ results }) => {
  return (
    <Group
      sx={{
        flexGrow: 1,
      }}
      title={"Partner Income Summary"}
    >
      <InterestDataTable results={results} />
    </Group>
  );
};

/**
 * Layout Sections
 */
const HeaderSection = ({ results }) => {
  const hideInterestRate = useMemo(() => {
    if (!isEmpty(results?.hard_exceptions)) {
      return true;
    }

    if (
      results?.sizerLoanToAfterRepairValue >
      results?.max_leverage?.["Max Initial LTARV"]
    ) {
      return true;
    }

    if (results?.sizerLoanToCost > results?.max_leverage?.["Max Initial LTC"]) {
      return true;
    }

    if (
      results?.sizerLoanToValue > results?.max_leverage?.["Max Initial LTV"]
    ) {
      return true;
    }

    return false;
  }, [results]);
  const invalidInterestRate = useMemo(() => {
    return results?.requestedRate < results?.required_interest_rate;
  }, [results]);
  return (
    <Flx
      fw
      wrap
      jb
      gap={1.5}
      sx={{
        mt: 0,

        mb: 3,
      }}
    >
      <Flx column gap={0.5}>
        <Htag sx={{ mt: 1 }}>Results</Htag>
        {invalidInterestRate ? (
          <Txt color={"error"} sx={{ fontSize: "11px" }}>
            Requested rate is below required rate for scenario inputs
          </Txt>
        ) : null}
      </Flx>

      <Flx gap={0.5} column>
        <Flx g={1}>
          {isNil(results?.requestedRate) ? null : (
            <HighlightItem
              label={"Requested Rate"}
              type="percent"
              value={results?.requestedRate}
              sx={{
                background: invalidInterestRate ? red[50] : "#d4edda",
                ".txt": {
                  color: invalidInterestRate ? red[700] : "#155724",
                },
              }}
            />
          )}
          {hideInterestRate ? null : (
            <HighlightItem
              label={"Floor Rate"}
              type="percent"
              value={results?.required_interest_rate}
            />
          )}
        </Flx>
      </Flx>
    </Flx>
  );
};

/**
 * Tables
 */
const ZillowDataTable = ({ results }) => {
  const [rowData, setRowData] = useState(null);

  const columnDefs = useMemo(
    () => [
      {
        field: "name",
        width: 160,

        flex: 0,
        wrapText: true,
        autoHeight: true,
        cellStyle: {
          fontWeight: cellNameFontWeight,
          borderRight: "1px solid #eeeeee",
          lineHeight: "1",
          display: "flex",
          alignItems: "center",
          paddingRight: "4px",
          paddingLeft: "8px",
        },
      },

      {
        field: "value",

        flex: 1,
        wrapText: true,
        autoHeight: true,
        cellRenderer: MinimalTableCellRenderer,
        cellStyle: () => {
          return {
            padding: 0,
          };
        },
      },
    ],
    []
  );

  useEffect(() => {
    const mapRowData = () => {
      return [
        {
          name: "Median",
          value: results?.zillowMedian,
          type: "dollar",
        },
        {
          name: "Multiplier",
          value: results?.zillowMultiplier,
          type: "float",
        },
        {
          name: "Home Price Appreciation",
          value: results?.zillowHPA,
          type: "percent",
        },
        {
          name: "Date",
          value: results?.zhviDate,
        },
        {
          name: "Metro",
          value: results?.zhviMetro,
        },
      ];
    };
    const mappedRowData = mapRowData();
    setRowData(mappedRowData);
  }, [results]);

  return <MinimalDisplayTable rowData={rowData} columnDefs={columnDefs} />;
};

const InterestDataTable = ({ results }) => {
  const [rowData, setRowData] = useState(null);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Leverage Type",
        field: "name",
        flex: 1,
        width: 220,
        cellStyle: {
          fontWeight: cellNameFontWeight,
          borderRight: "1px solid #eeeeee",
          lineHeight: "1",
          display: "flex",
          alignItems: "center",
          paddingRight: "4px",
          paddingLeft: "8px",
        },
      },
      {
        field: "value",
        minWidth: 85,
        width: 85,
        flex: 0,
        cellRenderer: MinimalTableCellRenderer,
        cellStyle: () => ({
          padding: 0,
        }),
      },
    ],
    []
  );

  useEffect(() => {
    if (!results) {
      setRowData([]);
      return;
    }

    const mapped = [
      {
        name: "Strip Interest",
        value: results.stripInterest,
        type: "percent",
      },
      {
        name: "Partner Strip Interest",
        value: results.partnerStripInterest,
        type: "percent",
      },

      {
        name: "Interest Income 12-month maturity",
        value: results.interestIncome12MonthMaturity,
        type: "dollar",
      },
      {
        name: "Partner Origination Income",
        value: results.partnerOriginationIncome,
        type: "dollar",
      },
    ];

    setRowData(mapped);
  }, [results]);

  return <MinimalDisplayTable rowData={rowData} columnDefs={columnDefs} />;
};

const BorrowerClassificationTable = ({ results }) => {
  const [rowData, setRowData] = useState(null);

  const columnDefs = useMemo(
    () => [
      {
        field: "name",
        flex: 1,
        cellStyle: {
          fontWeight: cellNameFontWeight,
          borderRight: "1px solid #eeeeee",
          lineHeight: "1",
          display: "flex",
          alignItems: "center",
          paddingRight: "4px",
          paddingLeft: "8px",
        },
      },

      {
        field: "value",

        width: 105,
        flex: 0,

        cellRenderer: MinimalTableCellRenderer,
        cellStyle: (params) => {
          const name = params.data.name;
          let bg = "#ffffff"; // Default background color
          if (name === "Overall Score") {
            if (
              results?.sizerBorrowers?.rtl_borrower_classification_score ===
              "A+"
            ) {
              bg = "#d4eddad6";
            }
            if (
              results?.sizerBorrowers?.rtl_borrower_classification_score === "A"
            ) {
              bg = "#fff4c9";
            }
            if (
              results?.sizerBorrowers?.rtl_borrower_classification_score === "B"
            ) {
              bg = "#ff9e005e";
            }
            if (
              results?.sizerBorrowers?.rtl_borrower_classification_score === "C"
            ) {
              bg = "#ef36004d";
            }
          }
          return {
            background: bg,
            padding: 0,
          };
        },
      },
    ],
    [results]
  );

  useEffect(() => {
    const mapRowData = () => {
      return [
        {
          name: "Overall Score",
          value: results?.sizerBorrowers?.rtl_borrower_classification_score,
        },
        {
          name: "Credit Decision Score",
          value: results?.sizerBorrowers?.sizerRTLCreditDecisionScore,
        },
        {
          name: "Experience Score",
          value: results?.sizerBorrowers?.sizerRTLExperienceScore,
        },
        {
          name: "Verified Liquidity",
          value: results?.sizerBorrowers?.sizerVerifiedlLiquidity,
          type: "dollar",
        },
      ];
    };
    const mappedRowData = mapRowData();
    setRowData(mappedRowData);
  }, [results]);

  return (
    <MinimalDisplayTable
      rowData={rowData}
      columnDefs={columnDefs}
      noBorder
      styles={{ borderRight: "1px solid #eeeeee", borderRadius: 0 }}
    />
  );
};

const LoanBreakdownTable = ({ results }) => {
  const [rowData, setRowData] = useState(null);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Leverage Type",
        field: "name",
        flex: 1,
        cellStyle: {
          fontWeight: cellNameFontWeight,
          borderRight: "1px solid #eeeeee",
          lineHeight: "1",
          display: "flex",
          alignItems: "center",
          paddingRight: "4px",
          paddingLeft: "8px",
        },
      },
      {
        field: "value",
        width: 105,
        flex: 0,
        cellRenderer: MinimalTableCellRenderer,
        cellStyle: () => {
          return {
            padding: 0,
          };
        },
      },
    ],
    []
  );

  const styles = useMemo(() => {
    return {
      p: 0,
      ".ag-minimial-cell": {
        textAlign: "right",
        pr: "16px",
      },
      '[data-name="Initial Loan Amount"], [data-name="Financed Rehab Budget"]':
        {
          fontWeight: "bold",
          textAlign: "right",
          pr: "6px",
        },
      '[data-name="Total Loan Amount"]': {
        fontWeight: "bold",
        textAlign: "right",
        pr: "32px",
      },
      '[data-name="Financed Budget Percent"]': {
        fontWeight: "bold",
        textAlign: "left",
      },
    };
  }, []);

  useEffect(() => {
    const mapRowData = () => {
      return [
        {
          name: "Initial Loan Amount",
          type: "dollar",
          value: results?.economics_payload?.sizerBaseLoanAmount,
        },
        {
          name: "As-is-Valuation",
          type: "dollar",
          value: results?.economics_payload?.sizerAsIsValue,
        },
        {
          name: "Purchase Price",
          type: "dollar",
          value: results?.economics_payload?.sizerPurchasePrice,
        },
        {
          name: "Additional Cost basis (incl. closing costs)",
          type: "dollar",
          value: results?.economics_payload?.sizerTotalLoanAmount,
        },
        {
          name: "Total Cost Basis",
          type: "dollar",
          value: results?.subjectProperty?.costBasis, // ??
        },
        {
          name: "Total Financed Budget",
          type: "dollar",
          value: results?.economics_payload?.sizerFinancedBudget,
        },
        {
          name: "Total Loan Amount",
          type: "dollar",
          value: results?.economics_payload?.sizerTotalLoanAmount,
        },
        {
          name: " ",
          type: "break",
        },
        {
          name: "Total Construction Budget",
          type: "dollar",
          value: results?.economics_payload?.sizerTotalRehabBudget,
        },
        {
          name: "After Renovation Value (ARV)",
          type: "dollar",
          value: results?.economics_payload?.sizerAfterRepairValue,
        },
      ];
    };
    const mappedRowData = mapRowData();
    setRowData(mappedRowData);
  }, [results]);

  return (
    <MinimalDisplayTable
      rowData={rowData}
      columnDefs={columnDefs}
      styles={styles}
    />
  );
};

const MaxLeverageTable = ({ results }) => {
  const [rowData, setRowData] = useState(null);

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Leverage Type",
        field: "name",
        flex: 1,
        cellStyle: { fontWeight: "bold" },
      },
      {
        headerName: "Requested Leverage",
        field: "requested",
        flex: 1,
        type: "percent",
        cellStyle: (params) => {
          const requested = params.value;
          const max = params.data.max;
          if (requested == null || max == null) {
            return null;
          }
          if (requested <= max) {
            return {
              backgroundColor: "#d4edda", // Light green background
              color: "#155724", // Dark green text
              fontWeight: "bold",
            };
          } else {
            return {
              backgroundColor: "#f8d7da", // Light red background
              color: "#721c24", // Dark red text
              fontWeight: "bold",
            };
          }
        },
        cellRenderer: LeverageCellRenderer,
      },
      {
        headerName: "Max Leverage",
        field: "max",
        type: "percent",
        flex: 1,
      },
    ],
    []
  );

  useEffect(() => {
    const mapRowData = () => {
      return [
        {
          name: "LTC",
          requested: results?.sizerLoanToCost,
          max: results?.max_leverage?.["Max Initial LTC"],
        },
        {
          name: "LTV",
          requested: results?.sizerLoanToValue,
          max: results?.max_leverage?.["Max Initial LTV"],
        },
        {
          name: "LTARV",
          requested: results?.sizerLoanToAfterRepairValue,
          max: results?.max_leverage?.["Max Initial LTARV"],
        },
      ];
    };
    const mappedRowData = mapRowData();
    setRowData(mappedRowData);
  }, [results]);

  return (
    <MinimalDisplayTable
      rowData={rowData}
      columnDefs={columnDefs}
      headerHeight={32}
      rowHeight={32}
    />
  );
};

const LeverageCellRenderer = (params) => {
  const value = params?.valueFormatted;
  const requested = params.value;
  const max = params.data.max;

  if (requested <= max) {
    return (
      <Flx ac gap={1}>
        <CheckRounded sx={{ color: "#155724", fontSize: "18px" }} />
        {value}
      </Flx>
    );
  }
  if (requested > max) {
    return (
      <Flx ac gap={1}>
        <ErrorOutline sx={{ color: "#721c24", fontSize: "18px" }} />
        {value}
      </Flx>
    );
  }
  return <>{value}</>;
};

const ResultRowListItem = ({ children, title, type, sx, items, value }) => {
  // Handle array items (like exceptions and reductions)
  if (Array.isArray(items)) {
    const hasItems = items && items.length > 0;
    const resultsIcon = hasItems ? (
      <ErrorOutline sx={{ color: red[400], fontSize: "18px" }} />
    ) : (
      <CheckRounded sx={{ color: green[400], fontSize: "18px" }} />
    );
    const resultsContent = hasItems
      ? items.map((item, index) => {
          const text =
            item?.reason || item?.description || JSON.stringify(item);
          return (
            <Flx
              ac
              key={index}
              sx={{
                p: 0.4,
                px: 1,
                flexShrink: 0,
                backgroundColor: red[50],
                borderRadius: 1,
              }}
            >
              <Txt variant="body2" sx={{ color: red[700] }}>
                {text || "Unknown Reason"}
              </Txt>
            </Flx>
          );
        })
      : null;

    return (
      <Flx ac wrap gap={1}>
        {resultsIcon}
        <Txt sx={{ flexShrink: 0, mr: 0.4, ml: 0.4 }} bold>
          {title}
        </Txt>
        {/* {resultsIcon} */}
        {resultsContent}
      </Flx>
    );
  }
};

const HighlightItem = ({ label, value, type, sx }) => {
  const getValue = () => {
    if (isNil(value)) {
      return "N/A"; // Skip rendering if value is null or undefined
    }
    if (value !== undefined && value !== null) {
      let displayValue;

      if (isNumber(value)) {
        switch (type) {
          case "percent":
            displayValue = `${(value * 100).toFixed(2)}%`;
            break;
          case "dollar":
            displayValue = `${formatDollar(value)}`;
            break;
          case "integer":
            displayValue = `${Math.round(value)}`;
            break;
          case "float":
            displayValue = `${value.toFixed(4)}`;
            break;
          default:
            displayValue = value.toString();
        }
      } else {
        displayValue = value.toString();
      }

      return displayValue;
    }

    return;
  };
  return (
    <Flx
      gap={0.5}
      sx={{ flexGrow: 1, flexShrink: 0, p: 1, borderRadius: 1, ...sx }}
      column
    >
      <SecondaryLabel>{label}</SecondaryLabel>
      <Txt sx={{ fontSize: "18px", fontWeight: 700, color: grey[900] }}>
        {/* <Txt sx={{ fontSize: "28px", fontWeight: 700, color: grey[900] }}> */}
        {getValue()}
      </Txt>
    </Flx>
  );
};

const GradeRadialDial = ({ grade = "A" }) => {
  const grades = ["C", "B", "A", "A+"];
  const gradeIndex = grades.indexOf(grade);
  const initialValue = gradeIndex !== -1 ? gradeIndex : 2;

  const [options, setOptions] = useState({
    type: "radial-gauge",
    value: initialValue,
    scale: {
      min: 0,
      max: 3,
      interval: 1,
      label: {
        formatter: ({ value }) => grades[value] || "",
      },
    },

    width: 240,
    height: 105,
    padding: {
      top: 15,
      right: 0,
      bottom: 0,
      left: 0,
    },
    bar: {
      enabled: true,
      // width: 10,
      thicknessRatio: 0.1,
      fills: [
        { color: red[500] }, // Red for C
        // { color: orange[500] }, // Orange for B
        { color: orange[500] }, // Green for A
        { color: amber[500] }, // Green for A
        // { color: green[500] }, // Blue for A+
        { color: green[500] }, // Blue for A+
      ],
      // fillMode: "discrete",
    },
    needle: {
      enabled: true,
      radiusRatio: 0.8,
      fill: "#333",
      stroke: "#333",
      strokeWidth: 2,
    },

    background: {
      fill: "transparent", // <-- make the chart background transparent :contentReference[oaicite:0]{index=0}
    },
    cornerRadius: 5,
    cornerMode: "container",
    label: {
      enabled: true,
      fontSize: 24,
      fontWeight: "bold",
      color: "#333",
      formatter: () => grades[Math.round(options.value)],
    },
  });

  // Update options when grade prop changes
  useEffect(() => {
    const newIndex = grades.indexOf(grade);
    const newValue = newIndex !== -1 ? newIndex : 2;

    setOptions((prev) => ({
      ...prev,
      value: newValue,
      label: {
        ...prev.label,
        formatter: () => grades[newValue],
      },
    }));
  }, [grade, grades]);

  return <AgGauge options={options} />;
};

const MinimalTableCellRenderer = (params) => {
  const value = params.data?.value;
  const name = params.data.name;
  const type = params.data.type;

  const renderedValue = useMemo(() => {
    const getValue = () => {
      if (isNil(value)) {
        return;
      }

      let displayValue;
      if (isNumber(value)) {
        switch (type) {
          case "percent":
            displayValue = `${(value * 100).toFixed(4)}%`;
            break;
          case "dollar":
            displayValue = new Intl.NumberFormat("en-US")?.format(
              value?.toFixed(2)
            );

            break;
          case "integer":
            displayValue = `${Math.round(value)}`;
            break;
          case "float":
            displayValue = `${value.toFixed(4)}`;
            break;
          default:
            displayValue = value.toString();
        }
      } else {
        displayValue = value.toString();
      }

      return displayValue;
    };

    if (isNil(value)) {
      return;
    }

    return getValue();
  }, [value, type]);

  return (
    <Box
      className="ag-minimial-cell"
      data-name={name}
      data-type={type}
      sx={{
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        pl: "4px",
        pr: "8px",
        textAlign: "right !important",
      }}
    >
      <AttachMoneyRounded
        className="thin-8"
        sx={{
          fontSize: "14px",
          visibility: !isNil(value) && type === "dollar" ? "visible" : "hidden",
        }}
      />

      {renderedValue}
    </Box>
  );
};

/**
 * UI Non-Stateful
 */

const TwoColumnRow = ({ children, gap = 4 }) => {
  return (
    <Flx wrap fw jb gap={gap}>
      {children}
    </Flx>
  );
};

const Group = ({ title, children, mt = 0, g = "4px", rootGap = 0.8, sx }) => {
  return (
    <Flx sx={{ mt: mt, ...sx }} column gap={rootGap}>
      <SecondaryLabel>{title}</SecondaryLabel>
      <Flx column gap={g}>
        {children}
      </Flx>
    </Flx>
  );
};

const SecondaryLabel = ({ children, sx }) => {
  return (
    <Txt
      sx={{
        fontSize: "13px",
        // fontSize: "14px",
        fontWeight: 400,
        color: grey[600],
        opacity: 0.9,
        ...sx,
      }}
    >
      {children}
    </Txt>
  );
};

const RootCard = ({ results, children }) => {
  const containerRef = useRef(null);
  // useEffect(() => {
  //   window.scrollTo({ top: 0, behavior: "smooth" });
  //   if (containerRef.current) {
  //     containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
  //   }
  // }, [results]);
  return (
    <Card
      ref={containerRef}
      sx={{
        height: "max-content",
        p: 3,
        flexShrink: 1,
        flexBasis: "720px",
        position: "sticky",
        top: "16px",
        left: 0,
      }}
    >
      {children}
    </Card>
  );
};

const MinimalDisplayTable = ({
  rowData,
  columnDefs,
  headerHeight = 0,
  rowHeight = 30,
  noBorder = false,
  styles,
}) => {
  const columnTypes = useColumnTypes();
  const defaultColDef = useMemo(() => {
    return {
      resizable: false,
      ...columnDefSuppressHeaderFilterButton,
    };
  }, []);

  const getRowHeight = useCallback((props) => {
    if (props?.data?.type === "break") {
      return 10;
    }
  }, []);
  const getRowStyle = useCallback((props) => {
    if (props?.data?.type === "break") {
      return {
        background: "#fafafa",
        // background: "#f5f5f5",
      };
    }
  }, []);
  return (
    <AgGridTableWrapper
      height="100%"
      simpleTable
      suppressMinHeightWhenPopulated={rowData}
      size={4}
      noBorder={noBorder}
      sx={{
        border: noBorder ? "none" : "1px solid #eeeeee",
        borderBottom: "none",
        overflow: "hidden",
        borderRadius: "8px",
        ".ag-header": {
          display: headerHeight === 0 ? "none" : "flex",
          fontStyle: "italic",
        },
        ".ag-header-cell-text": {
          fontWeight: 400,
          // color: "#757575",
        },
        ...styles,
      }}
    >
      <AgGridReact
        rowData={rowData}
        columnDefs={columnDefs}
        singleClickEdit
        getRowHeight={getRowHeight}
        domLayout={"autoHeight"}
        rowHeight={rowHeight}
        getRowStyle={getRowStyle}
        defaultColDef={defaultColDef}
        columnTypes={columnTypes}
        headerHeight={headerHeight}
      />
    </AgGridTableWrapper>
  );
};

const MinimalCard = ({ children, sx }) => {
  return (
    <Box
      sx={{
        border: "1px solid #eeeeee",
        borderRadius: "8px",
        overflow: "hidden",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

export default LoanIntakePricingSizerResultsCard;
